// A page of this site is a file of its own, fetched the first time the page is
// opened. That fetch can fail: the connection dropped for a moment, an in-app
// browser gave up on it, or the tab has been open since before a deploy.
//
// Vite announces each such failure with a `vite:preloadError` event on window
// and then lets the import() fail. This module takes note of the error, so the
// router can tell a page that did not load from a bug and load the site again
// onto that page (router/index.ts), and the error report can leave it out
// (error_handler.ts).
//
// The listener must never call event.preventDefault(). That tells Vite the
// failure has been dealt with, and the import() then resolves to `undefined`
// instead of failing. vue-router reports that as
//     Couldn't resolve component "default" at "/account-settings"
// and the page stays closed with nothing said to the user.

const RELOAD_KEY = "chunk-reload";

const failed = new WeakSet<object>();

window.addEventListener("vite:preloadError", (event) => {
    const err: unknown = event.payload;
    if (err && typeof err === "object") failed.add(err);
});

// The page this load of the site was started for by reloadOnto(), if it was.
// Read once and taken out of the storage, so it speaks for this load only.
let reloadedOnto: string | null = null;
try {
    reloadedOnto = sessionStorage.getItem(RELOAD_KEY);
    sessionStorage.removeItem(RELOAD_KEY);
} catch {
    // No session storage in this browser: reloadOnto() does nothing.
}

/** Whether this error is a file of a page that did not load. */
export function isChunkLoadError(err: unknown): boolean {
    return !!err && typeof err === "object" && failed.has(err);
}

/**
 * Loads the site again, onto `href`. A full page load fetches index.html anew,
 * and with it the file names of the build that is deployed now.
 *
 * Returns false, and does nothing, when this load of the site was itself
 * started for that page: loading again did not help, and once more would go
 * round in circles. The same where the browser gives no session storage,
 * because nothing could stop the circle there.
 */
export function reloadOnto(href: string): boolean {
    if (reloadedOnto === href) return false;
    try {
        sessionStorage.setItem(RELOAD_KEY, href);
    } catch {
        return false;
    }
    window.location.assign(href);
    return true;
}
